import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import Like from './Like';
import WebToAppBanner from '../../common/component/WebToAppBanner';
import {REGION} from '../../common/constant';
import useTypedSelector from '../../common/hook/useTypedSelector';
import {
  getTorosCustomProps,
  getTorosImpContents,
} from '../../common/util/tracking';
import EpisodeButtonV4 from '../../commonSub/component/EpisodeButtonV4';
import MaxWidth from '../../commonSub/component/MaxWidth';
import {WEB_TO_APP_EVENT_PROPS} from '../../commonSub/constant';
import useViewerSettings from '../../commonSub/hook/useViewerSettings';
import {IRecommendItemContent} from '../../commonSub/type';
import {USE_TYPE_TEXT_MAP} from '../../content/common';
import {sendViewerEvent, sendViewerViewImp} from '../common';
import Comments from '../component/viewerEnd/Comments';
import OriginalProduct from '../component/viewerEnd/OriginalProduct';
import RecommendContents from '../component/viewerEnd/RecommendContents';
import ViewerBigBanner from '../component/viewerEnd/ViewerBigBanner';
import {actions} from '../state/actions';

interface IProps {
  className?: string;
  goToNextEpisode: () => void;
}

export default function ViewerEnd({className, goToNextEpisode}: IProps) {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const [
    episodeId,
    contentId,
    viewerComment,
    recommendContents,
    extendBanner,
    originalProduct,
    nextEpisode,
  ] = useTypedSelector(({viewer}) => [
    viewer.episode?.id,
    viewer.episode?.contentId,
    viewer.viewerEnd?.viewerComment,
    viewer.viewerEnd?.recommendContents,
    viewer.viewerEnd?.extendBanner,
    viewer.viewerEnd?.originalProduct,
    viewer.nextEpisode,
  ]);

  const [{isPreviewComment, viewType}] = useViewerSettings(contentId);

  const showRecommendContents = !!recommendContents?.items?.length;
  const showBestComments =
    isPreviewComment && !!viewerComment?.comments && !!contentId && !!episodeId;

  return (
    <MaxWidth
      hasMdWidth
      className={cn('flex-none relative z-1 px-11 !mt-20', className)}
    >
      {REGION === 'kor' && extendBanner && viewType === 'scroll' && (
        <ViewerBigBanner viewType={viewType} />
      )}
      <Like viewType={viewType} />
      {showBestComments && (
        <div className="bg-grey-01 light:bg-background rounded-12 px-18">
          <Comments
            contentId={contentId}
            episodeId={episodeId}
            viewType={viewType}
            comments={
              viewType === 'page'
                ? viewerComment.comments.slice(0, 1)
                : viewerComment.comments
            }
            totalCount={viewerComment.totalCount}
            showMoreButton={viewerComment.comments.some(
              comment => comment.isBest
            )}
            openCommentModal={() => dispatch(actions.setViewerState('comment'))}
          />
        </div>
      )}
      {nextEpisode && (
        <EpisodeButtonV4
          className="mt-20 pl-10 pr-18 py-10"
          title={t('viewer_end_next_button_v4', {value: nextEpisode.title})}
          subTextFirst={t(USE_TYPE_TEXT_MAP[nextEpisode.type] || '')}
          subTextSecond={nextEpisode.subText}
          thumbnail={nextEpisode.thumbnail}
          onClick={() => {
            sendViewerEvent({
              actionName: 'next_episode',
              action: 'click',
              customProps: {
                content_id: nextEpisode.contentId,
                episode_id: nextEpisode.id,
                viewer_type: viewType,
                event_layer: 'next_ep',
              },
            });
            goToNextEpisode();
          }}
        />
      )}
      {originalProduct &&
        !nextEpisode &&
        viewType === 'scroll' &&
        episodeId && (
          <OriginalProduct
            originalProduct={originalProduct}
            episodeId={episodeId}
          />
        )}
      {viewType === 'scroll' && !!recommendContents?.items?.length && (
        <RecommendContents
          contents={recommendContents.items}
          sendClickEvent={(content: IRecommendItemContent, index: number) =>
            sendViewerEvent({
              actionName: 'content',
              action: 'click',
              customProps: {
                content_id: content.id,
                episode_id: episodeId,
                event_layer: 'recommend',
                viewer_type: viewType,
                ...getTorosCustomProps(recommendContents.torosInfo, {
                  toros_click_ordnum: index + 1,
                  toros_page_meta_id: contentId,
                  toros_event_meta_id: content.id,
                }),
              },
            })
          }
          sendViewImp={(content, index) => {
            sendViewerViewImp({
              customProps: {
                content_id: contentId,
                episode_id: episodeId,
                event_layer: 'recommend',
                viewer_type: viewType,
                ...getTorosCustomProps(recommendContents.torosInfo, {
                  toros_page_meta_id: contentId,
                  toros_event_meta_id: content.id,
                }),
              },
              viewImpContents: getTorosImpContents(
                recommendContents.torosInfo,
                {
                  imp_ordnum: index + 1,
                  id: content.id,
                  type: 'content_id',
                  name: 'content',
                }
              ),
            });
          }}
        />
      )}
      {viewType === 'scroll' && (
        <WebToAppBanner
          isViewerPage
          className={cn('px-0', showRecommendContents ? 'mt-10' : 'mt-20')}
          bannerClassName="rounded-12"
          onIntersection={() =>
            sendViewerViewImp({customProps: WEB_TO_APP_EVENT_PROPS})
          }
          onSendEvent={() =>
            sendViewerEvent({
              action: 'click',
              actionName: 'banner',
              customProps: WEB_TO_APP_EVENT_PROPS,
            })
          }
        />
      )}
    </MaxWidth>
  );
}
